/**
 * Enhanced SRT Parser
 * Handles standard SRT format with improved robustness for various formatting issues
 */

/**
 * Parses SRT timestamp format (00:00:00,000) to seconds
 * @param {string} timestamp - SRT format timestamp
 * @returns {number} Time in seconds
 */
const parseTimestamp = (timestamp) => {
  // Handle both comma and period as decimal separators
  const normalizedTimestamp = timestamp.replace(',', '.');
  
  // Handle both formats: 00:00:00.000 and 00:00.000
  const parts = normalizedTimestamp.split(':');
  
  if (parts.length === 3) {
    // Format: 00:00:00.000 (hours:minutes:seconds.milliseconds)
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const secondsParts = parts[2].split('.');
    const seconds = parseInt(secondsParts[0], 10);
    const milliseconds = secondsParts[1] ? parseInt(secondsParts[1].padEnd(3, '0'), 10) : 0;
    
    return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
  } else if (parts.length === 2) {
    // Format: 00:00.000 (minutes:seconds.milliseconds)
    const minutes = parseInt(parts[0], 10);
    const secondsParts = parts[1].split('.');
    const seconds = parseInt(secondsParts[0], 10);
    const milliseconds = secondsParts[1] ? parseInt(secondsParts[1].padEnd(3, '0'), 10) : 0;
    
    return minutes * 60 + seconds + milliseconds / 1000;
  }
  
  // If format unrecognized, return 0
  console.warn(`Unrecognized timestamp format: ${timestamp}`);
  return 0;
};

/**
 * Parse SRT content into an array of subtitle objects
 * @param {string} srtContent - Raw SRT file content
 * @returns {Array} Array of subtitle objects with index, start, end, and text
 */
export const parseSRT = (srtContent) => {
  if (!srtContent || typeof srtContent !== 'string') {
    console.error('Invalid SRT content provided');
    return [];
  }
  
  // Normalize line endings
  const normalizedContent = srtContent.replace(/\r\n|\r/g, '\n');
  
  // Split by double newline to get subtitle blocks
  const blocks = normalizedContent.split('\n\n').filter(block => block.trim() !== '');
  
  return blocks.map((block) => {
    const lines = block.split('\n').filter(line => line.trim() !== '');
    
    if (lines.length < 2) {
      console.warn('Invalid subtitle block:', block);
      return null;
    }
    
    // First line should be the index number
    const indexLine = lines[0];
    const index = parseInt(indexLine.trim(), 10);
    
    // Second line should contain timestamps
    const timestampLine = lines[1];
    const timestampParts = timestampLine.split('-->').map(part => part.trim());
    
    if (timestampParts.length !== 2) {
      console.warn('Invalid timestamp format:', timestampLine);
      return null;
    }
    
    const startTime = parseTimestamp(timestampParts[0]);
    const endTime = parseTimestamp(timestampParts[1]);
    
    // Remaining lines are the subtitle text
    const text = lines.slice(2).join('\n');
    
    return {
      index,
      start: startTime,
      end: endTime,
      text
    };
  }).filter(subtitle => subtitle !== null);
};

/**
 * Find subtitle at specific time
 * @param {Array} subtitles - Array of subtitle objects
 * @param {number} time - Current time in seconds
 * @returns {Object|null} Subtitle object or null if none active
 */
export const findSubtitleAtTime = (subtitles, time) => {
  if (!subtitles || !Array.isArray(subtitles) || subtitles.length === 0) {
    return null;
  }
  
  return subtitles.find(sub => time >= sub.start && time <= sub.end) || null;
};

export default {
  parseSRT,
  findSubtitleAtTime
};